import { cloneDeep, isEmpty, pickBy, values } from 'lodash';
import Constants from '../../Constants';
import * as actionTypes from '../../constants/actionTypes';
import {
  INSERT_TYPE_BULK_INSERT,
  INSERT_TYPE_TABLE,
  WRITE_MODE_OVERWRITE,
} from '../../constants/dataMapConstants';
import { DELETE, GET, POST, PUT } from '../../constants/requestMethods';
import {
  ACLQueryModeTypes,
  RESOURCE_SUGGESTIONS_COUNT,
  SearchFilterViews,
} from '../../constants/visibilityFilters';
import { getDataMapFromCSV } from '../../lib/DataMapsProcessor';
import { processSearchFilters } from '../../lib/SearchFilterUtils';
import { isStateStale } from '../../lib/StateUtils';
import { CALL_PRIVATE_API } from '../../middleware/api';
import { dataMapSchema, docSchema } from '../../schemas';

export const loadDataMapListing =
  (page, sortCriteria, availableFilters) => (dispatch, getState) => {
    const {
      app: {
        accessControl,
        visibilityFilters: { filters },
      },
    } = getState();

    const aclQueryMode = accessControl.mode ?? ACLQueryModeTypes.MODE_OWNER.key;
    const { sortKey, sortOrder } = Constants.SortCriteriaInfo[sortCriteria];
    const { processedFilters } = processSearchFilters(
      filters[SearchFilterViews.DATA_MAPS],
      availableFilters,
    );
    let requestMethod;
    let endpoint;
    let payload;

    if (isEmpty(processedFilters)) {
      requestMethod = GET;
      endpoint = '/data_maps';
      payload = {
        page,
        per_page: Constants.LISTING_ITEMS_PER_PAGE,
        sort_by: sortKey,
        sort_order: sortOrder,
      };
    } else {
      requestMethod = POST;
      endpoint = `/data_maps/search?page=${page}&per_page=${Constants.LISTING_ITEMS_PER_PAGE}&sort_by=${sortKey}&sort_order=${sortOrder}`;
      payload = { filters: processedFilters };
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_DATA_MAPS_REQUEST,
          actionTypes.GET_DATA_MAPS_SUCCESS,
          actionTypes.GET_DATA_MAPS_FAILURE,
        ],
        endpoint,
        schema: [dataMapSchema],
        requestMethod,
        aclQueryMode,
        payload,
        requestInfo: { view: SearchFilterViews.DATA_MAPS },
      },
    }).then((response) => {
      return response;
    });
  };

export const loadDataMaps =
  (
    forceReload = false,
    //Lookups usually are low cost and used during resource setup so fetch for all acl modes
    aclQueryMode = ACLQueryModeTypes.MODE_COLLABORATOR.key,
  ) =>
  (dispatch, getState) => {
    if (forceReload || isStateStale(getState().dataMaps.lastUpdated)) {
      return dispatch({
        [CALL_PRIVATE_API]: {
          types: [
            actionTypes.GET_ALL_DATA_MAPS_REQUEST,
            actionTypes.GET_ALL_DATA_MAPS_SUCCESS,
            actionTypes.GET_ALL_DATA_MAPS_FAILURE,
          ],
          endpoint: `/data_maps`,
          schema: [dataMapSchema],
          requestMethod: GET,
          aclQueryMode,
        },
      });
    }
  };

export const loadDataMapsSearchSuggestions =
  (filters, availableFilters) => (dispatch, getState) => {
    const { app } = getState();
    const aclQueryMode = app.accessControl.mode;

    const { sortKey, sortOrder } =
      Constants.SortCriteriaInfo[Constants.SortCriteria.LAST_CREATED_DESC];
    const { processedFilters } = processSearchFilters(filters, availableFilters);

    if (isEmpty(processedFilters)) {
      return;
    }

    const params = new URLSearchParams({
      page: 1,
      per_page: RESOURCE_SUGGESTIONS_COUNT,
      sort_by: sortKey,
      sort_order: sortOrder,
    });

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.SEARCH_DATA_MAPS_REQUEST,
          actionTypes.SEARCH_DATA_MAPS_SUCCESS,
          actionTypes.SEARCH_DATA_MAPS_FAILURE,
        ],
        endpoint: `/data_maps/search?${params}`,
        schema: [dataMapSchema],
        requestMethod: POST,
        aclQueryMode,
        payload: { filters: processedFilters },
        requestInfo: { view: SearchFilterViews.DATA_MAPS },
      },
    });
  };

export const loadDataMap =
  (
    id, //Lookups usually are low cost and used during resource setup so fetch for all acl modes
    { aclQueryMode, expand, ignoreErrors } = {
      aclQueryMode: ACLQueryModeTypes.MODE_OWNER.key,
    },
  ) =>
  (dispatch) => {
    const payload = {};

    if (expand) {
      payload.expand = 1;
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_DATA_MAP_REQUEST,
          actionTypes.GET_DATA_MAP_SUCCESS,
          actionTypes.GET_DATA_MAP_FAILURE,
        ],
        endpoint: `/data_maps/${id}`,
        schema: dataMapSchema,
        requestMethod: GET,
        ignoreErrors,
        payload,
        aclQueryMode,
      },
    });
  };

export const deleteDataMap = (id) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.DELETE_DATA_MAP_REQUEST,
        actionTypes.DELETE_DATA_MAP_SUCCESS,
        actionTypes.DELETE_DATA_MAP_FAILURE,
      ],
      endpoint: `/data_maps/${id}`,
      schema: dataMapSchema,
      requestMethod: DELETE,
      requestInfo: {
        id,
      },
    },
  });
};

export const createDataMap =
  (newInfo = {}) =>
  (dispatch) => {
    const { map_entry_schema, id, ...payload } = newInfo;

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.CREATE_DATA_MAP_REQUEST,
          actionTypes.CREATE_DATA_MAP_SUCCESS,
          actionTypes.CREATE_DATA_MAP_FAILURE,
        ],
        endpoint: `/data_maps`,
        schema: dataMapSchema,
        requestMethod: POST,
        payload: payload,
      },
    });
  };

export const updateDataMap =
  (mapId, newInfo = {}) =>
  (dispatch) => {
    let { map_entry_schema, id, ...payload } = newInfo;
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.UPDATE_DATA_MAP_REQUEST,
          actionTypes.UPDATE_DATA_MAP_SUCCESS,
          actionTypes.UPDATE_DATA_MAP_FAILURE,
        ],
        endpoint: `/data_maps/${mapId}`,
        schema: dataMapSchema,
        requestMethod: PUT,
        payload: payload,
      },
    });
  };

export const loadDataMapSamples =
  (id, page = 1, perPage = 10) =>
  (dispatch) => {
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_DATA_MAP_SAMPLES_REQUEST,
          actionTypes.GET_DATA_MAP_SAMPLES_SUCCESS,
          actionTypes.GET_DATA_MAP_SAMPLES_FAILURE,
        ],
        endpoint: `/data_maps/${id}/probe/sample?page=${page}&per_page=${perPage}`,
        requestMethod: POST,
        payload: {
          'id.field.name': '*',
        },
        requestInfo: {
          id,
        },
      },
    });
  };

export const loadDataMapEntry = (id, key) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_DATA_MAP_ENTRY_REQUEST,
        actionTypes.GET_DATA_MAP_ENTRY_SUCCESS,
        actionTypes.GET_DATA_MAP_ENTRY_FAILURE,
      ],
      endpoint: `/data_maps/${id}/get_entries`,
      requestMethod: POST,
      payload: [key],
      requestInfo: {
        id,
      },
    },
  });
};

export const saveDataMapEntries = (id, newRows, editedRows) => (dispatch) => {
  const entries = [...values(editedRows), ...newRows];

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.SAVE_DATA_MAP_ENTRIES_REQUEST,
        actionTypes.SAVE_DATA_MAP_ENTRIES_SUCCESS,
        actionTypes.SAVE_DATA_MAP_ENTRIES_FAILURE,
      ],
      endpoint: `/data_maps/${id}/entries`,
      requestMethod: PUT,
      payload: {
        entries,
      },
    },
  });
};

export const deleteDataMapEntries = (id, deletedRows) => (dispatch) => {
  const entries = Object.keys(deletedRows);

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.DELETE_DATA_MAP_ENTRIES_REQUEST,
        actionTypes.DELETE_DATA_MAP_ENTRIES_SUCCESS,
        actionTypes.DELETE_DATA_MAP_ENTRIES_FAILURE,
      ],
      endpoint: `/data_maps/${id}/entries`,
      requestMethod: DELETE,
      payload: entries,
    },
  });
};

export const fetchDataMapDoc = (id) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_DATA_MAP_DOC_REQUEST,
        actionTypes.GET_DATA_MAP_DOC_SUCCESS,
        actionTypes.GET_DATA_MAP_DOC_FAILURE,
      ],
      endpoint: `/data_maps/${id}/docs`,
      schema: [docSchema],
      requestMethod: GET,
      payload: {
        expand: 1,
      },
      requestInfo: {
        id,
      },
    },
  });
};

export const updateDataMapDoc = (id, content) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UPDATE_DATA_MAP_DOC_REQUEST,
        actionTypes.UPDATE_DATA_MAP_DOC_SUCCESS,
        actionTypes.UPDATE_DATA_MAP_DOC_FAILURE,
      ],
      endpoint: `/data_maps/${id}/docs?expand=1`,
      schema: [docSchema],
      requestMethod: POST,
      payload: {
        docs: [
          {
            name: `Doc for lookup ${id}`,
            description: `Doc for lookup ${id}`,
            doc_type: 'md',
            text: content,
          },
        ],
      },
      requestInfo: {
        id,
      },
    },
  });
};

export const saveDataMapFromForm = (info) => (dispatch, getState) => {
  const {
    dataMapEditForm: { deletedRows, editedRows, newRows, bulkText, insertType, writeMode },
  } = getState();
  const { id, map_primary_key, data_map } = info;

  const isOverwriteMode = !id || writeMode === WRITE_MODE_OVERWRITE;
  let bulkEntries;
  let saveError = null;
  let savedDataMapId;

  dispatch(beginDataMapSave());

  return new Promise((resolve, reject) => {
    if (insertType === INSERT_TYPE_BULK_INSERT) {
      getDataMapFromCSV(bulkText)
        .then((dataMapRows) => {
          bulkEntries = dataMapRows;
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    } else {
      resolve();
    }
  })
    .then(() => {
      const dataMapPayload = { ...info };

      if (!isOverwriteMode || insertType === INSERT_TYPE_TABLE) {
        delete dataMapPayload.data_map;
      } else if (insertType === INSERT_TYPE_BULK_INSERT) {
        dataMapPayload.data_map = bulkEntries;
      }

      return dispatch(id ? updateDataMap(id, dataMapPayload) : createDataMap(dataMapPayload));
    })
    .then((response) => {
      savedDataMapId = response.result;

      if (insertType === INSERT_TYPE_TABLE) {
        // create/update/delete entries
        const nonDeletedEditedRows = pickBy(editedRows, (value, key) => !deletedRows[key]);
        const rowsToDelete = {
          ...deletedRows,
          ...pickBy(editedRows, (value, key) => key !== value[map_primary_key]),
        };
        const hasModifiedRows = !isEmpty(newRows) || !isEmpty(nonDeletedEditedRows);
        const hasDeletedRows = !isEmpty(rowsToDelete);

        if (hasDeletedRows) {
          return dispatch(deleteDataMapEntries(savedDataMapId, rowsToDelete))
            .then(() => {
              dispatch(resetDeletedDataMapRows());
            })
            .finally(() => {
              if (hasModifiedRows) {
                return dispatch(
                  saveDataMapEntries(savedDataMapId, newRows, nonDeletedEditedRows),
                ).then(() => {
                  dispatch(resetNewDataMapRows());
                  dispatch(resetEditedDataMapRows());
                });
              }
            });
        }

        if (hasModifiedRows) {
          return dispatch(saveDataMapEntries(savedDataMapId, newRows, nonDeletedEditedRows)).then(
            () => {
              dispatch(resetNewDataMapRows());
              dispatch(resetEditedDataMapRows());
            },
          );
        }
      } else if (!isOverwriteMode) {
        const newEntries = bulkEntries || data_map;

        if (!isEmpty(newEntries)) {
          return dispatch(saveDataMapEntries(savedDataMapId, newEntries));
        }
      }
    })
    .catch((error) => {
      saveError = error.message;
    })
    .finally(() => {
      dispatch(endDataMapSave(saveError));

      if (saveError && (savedDataMapId || id)) {
        dispatch(
          loadDataMap(savedDataMapId || id, {
            aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
          }),
        );
      }
    });
};

export const clearDataMapSearchResults = () => {
  return {
    type: actionTypes.DATA_MAP_CLEAR_SEARCH_RESULTS,
  };
};

export const beginDataMapSave = () => {
  return {
    type: actionTypes.BEGIN_DATA_MAP_SAVE,
  };
};

export const endDataMapSave = (error = null) => {
  return {
    type: actionTypes.END_DATA_MAP_SAVE,
    payload: { error },
  };
};

export const clearStateToInitial = () => {
  return {
    type: actionTypes.CLEAR_DATA_MAP_STATE_TO_INITIAL,
  };
};

export const setInitialStateDataMapEditor = (dataMapObj, id, isTypeStatic) => {
  return {
    type: actionTypes.DATA_MAP_SET_INITIAL_STATE,
    payload: { dataMapObj, id, isTypeStatic },
  };
};

export const resetDeletedDataMapRows = () => {
  return {
    type: actionTypes.RESET_DELETED_DATA_MAP_ROWS,
  };
};

export const resetNewDataMapRows = () => {
  return {
    type: actionTypes.RESET_NEW_DATA_MAP_ROWS,
  };
};

export const resetEditedDataMapRows = () => {
  return {
    type: actionTypes.RESET_EDITED_DATA_MAP_ROWS,
  };
};

export const updateDataMapMeta = (key, value) => {
  return {
    type: actionTypes.UPDATE_DATA_MAP_META,
    payload: {
      key,
      value,
    },
  };
};

export const onSetSearchTerm = (payload) => {
  return {
    type: actionTypes.SET_DATA_MAP_SEARCH_TERM,
    payload,
  };
};

// POP UP FORM ACTIONS

export const deleteKey = () => {
  return {
    type: actionTypes.DELETE_DATA_MAP_PROP_KEY,
  };
};

export const toggleKeyForm = (title, buttonLabel, id, property) => {
  let property1 = cloneDeep(property); // prevents the form from changing the property key name before submission
  return {
    type: actionTypes.TOGGLE_DATA_MAP_KEY_FORM,
    payload: { title, buttonLabel, id, property: property1 },
  };
};

export const onFieldUpdated = (fieldName, text) => {
  return {
    type: actionTypes.UPDATE_DATA_MAP_FIELD,
    payload: { fieldName, text },
  };
};

export const onSubmitPropertyForm = () => {
  return {
    type: actionTypes.SET_DATA_MAP_PROPERTY_KEY,
  };
};

export const closeKeyForm = () => {
  return {
    type: actionTypes.CLOSE_DATA_MAP_KEY_FORM,
  };
};

// Bulk V Table insert actions
export const setInsertType = (insertType) => {
  return {
    type: actionTypes.SET_DATA_MAP_INSERT_TYPE,
    payload: { insertType },
  };
};

export const setWriteMode = (writeMode) => {
  return {
    type: actionTypes.SET_DATA_MAP_WRITE_MODE,
    payload: { writeMode },
  };
};

export const onChangeBulkInsert = (dataText) => {
  return {
    type: actionTypes.EDIT_DATA_MAP_BULK_INSERT_TEXT,
    payload: { dataText },
  };
};

export const onChangeSelectedFile = (fileContent, dataMapRows) => {
  return {
    type: actionTypes.EDIT_DATA_MAP_SELECTED_FILE,
    payload: { fileContent, dataMapRows },
  };
};

export const setDefaultValue = (propertyName, value) => {
  return {
    type: actionTypes.SET_DATA_MAP_DEFAULT,
    payload: { propertyName, value },
  };
};

export const setPrimaryKey = (propertyName) => {
  return {
    type: actionTypes.SET_DATA_MAP_PRIMARY_KEY,
    payload: { propertyName },
  };
};

// TABLE, ROWS, AND CELL VALUES ACTIONS:

export const changeDataMapCell = (columnPropertyName, value, rowKey, rowData) => {
  return {
    type: actionTypes.UPDATE_DATA_MAP_ROW_CELL,
    payload: { columnPropertyName, value, rowKey, rowData },
  };
};

export const deleteRow = (rowKey) => {
  return {
    type: actionTypes.DELETE_DATA_MAP_ROW,
    payload: { rowKey },
  };
};

export const undoDeleteRow = (rowKey) => {
  return {
    type: actionTypes.UNDO_DELETE_DATA_MAP_ROW,
    payload: { rowKey },
  };
};

export const addAnotherNewRow = () => {
  return {
    type: actionTypes.ADD_A_NEW_DATA_MAP_ROW,
  };
};

export const updateRowBeingAdded = (columnPropertyName, rowIdx, value) => {
  return {
    type: actionTypes.UPDATE_DATA_MAP_ROW_BEING_ADDED,
    payload: { columnPropertyName, rowIdx, value },
  };
};

export const deleteNewRow = (rowIdx) => {
  return {
    type: actionTypes.DELETE_DATA_MAP_ROW_BEING_ADDED,
    payload: { rowIdx },
  };
};

// PAGINATION
export const onFirstPage = () => {
  return {
    type: actionTypes.SET_DATA_MAP_KEY_PAGE_FIRST,
  };
};

export const onPrevPage = () => {
  return {
    type: actionTypes.SET_DATA_MAP_KEY_PAGE_PREV,
  };
};

export const onSetPage = (page) => {
  return {
    type: actionTypes.SET_DATA_MAP_KEY_PAGE,
    payload: { page },
  };
};

export const onNextPage = () => {
  return {
    type: actionTypes.SET_DATA_MAP_KEY_PAGE_NEXT,
  };
};

export const onLastPage = () => {
  return {
    type: actionTypes.SET_DATA_MAP_KEY_PAGE_LAST,
  };
};

export const setDynamicDataMapRows = (dynamicDataMapRows) => {
  return {
    type: actionTypes.SET_DYNAMIC_DATA_MAP_ROWS,
    payload: { dynamicDataMapRows },
  };
};
